import React from 'react';
import { Box, Container, Typography, List, ListItem, ListItemContent, Link, Divider } from '@mui/joy';
import { animate, stagger } from 'motion';
import { useEffect, useRef } from 'react';

const Cancellation = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const elements = containerRef.current.querySelectorAll('.animate-in');
        
        animate(
            elements,
            { 
                opacity: [0, 1],
                y: [20, 0]
            },
            { 
                delay: stagger(0.1),
                duration: 0.6,
                easing: 'ease-out'
            }
        );
    }, []);

    return (
        <Box ref={containerRef}>
            <Container sx={{ py: 8 }}>
                <Box className="animate-in" sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        level="h1"
                        fontSize="xl4"
                        sx={{
                            mb: 3,
                            bgcolor: 'text.primary',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Cancellation Policy
                    </Typography>
                    <Typography 
                        level="body1" 
                        fontSize="lg" 
                        sx={{ 
                            maxWidth: 800, 
                            mx: 'auto',
                            color: 'text.secondary',
                            lineHeight: 1.6
                        }}
                    >
                        At Hubitable, we make cancelling your account straightforward. This policy outlines your rights and the process, ensuring compliance with UK regulations.
                    </Typography>
                </Box>

                <Box className="animate-in" sx={{ maxWidth: 800, mx: 'auto' }}>
                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        1. How to Cancel
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Cancel directly via the cancellation link in each service's settings—no questions asked. You may choose to export your data beforehand.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Alternatively, email{' '}
                                    <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                                        Hubitable Support
                                    </Link> for assistance.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Note</strong>: An email or phone request alone does not cancel your account; you must use the official process.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        2. Cooling-Off Period for UK Customers
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Under the <strong>Consumer Contracts Regulations 2013</strong>, UK customers have a 14-day cooling-off period from the start of a paid subscription. Cancel within this period for a full refund, even if you've used the service.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Use the standard cancellation process and request a refund via{' '}
                                    <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                                        Hubitable Support
                                    </Link>.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        3. Cancellations After the Cooling-Off Period
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Cancelling after 14 days takes effect immediately, with no further charges. We do not prorate or refund unused time in the last billing cycle.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    For refunds due to faulty services, see our{' '}
                                    <Link href="/terms/refund" sx={{ color: 'primary.500' }}>
                                        Refund Policy
                                    </Link>.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        4. What Happens to Your Data
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    After cancellation:
                                </Typography>
                                <List size="sm" sx={{ mt: 2 }}>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Your content becomes inaccessible immediately.
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Within 30 days, all content is permanently deleted from active systems and logs.
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Within 60 days, all content is removed from backups.
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                </List>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Export your data beforehand using service-specific instructions indicated in the service's settings.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        5. Auto-Cancellation for Non-Payment
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    If payment fails, your account is frozen after a grace period. If unpaid for 30 days, it's queued for auto-cancellation, with data deleted per the above timelines.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>
                </Box>
            </Container>
        </Box>
    );
};

export default Cancellation; 